<template>
  <div
    v-loading="loading"
    class="container"
  >
    <BHeightAdjuster class="adjuster">
      <BLayout
        align-center
        class="header"
      >
        <TranscriptionHeader
          :call-result="callResult"
          :back-to-lead="backToLead"
          :can-go-back="canGoBack"
        />
      </BLayout>
      <BLayout class="body">
        <BFlex
          :span="6"
          class="body-left"
        >
          <SidePanel
            :zoom-phone-result-id="callResult?.zoomPhoneResultId"
            :transcription-exists="transcriptionExists"
            :is-transcription-result-empty="isTranscriptionResultEmpty"
            :transcription-in-progress="transcriptionInProgress"
            :right-after-transcription="rightAfterTranscription"
            @click:play-at="playAudio"
            @update:summarized-result="handleUpdateSummarizedResult"
          />
        </BFlex>
        <BFlex
          :span="12"
          class="body-center"
        >
          <TranscriptionMainContent
            ref="transcriptionMainContent"
            :transcription="transcription"
            :transcription-exists="transcriptionExists"
            :is-transcription-result-empty="isTranscriptionResultEmpty"
            :transcription-in-progress="transcriptionInProgress"
            :call-result="callResult"
            :audio-url="audioUrl"
            :audio-unavailable="audioUnavailable"
          />
        </BFlex>
        <BFlex
          :span="6"
          class="body-right"
        >
          <CallResultComment
            :call-result="callResult"
            :summarized-result="summarizedResult"
            @update:comment="handleUpdateComment"
            @update:loading="handleUpdateLoading"
          />
        </BFlex>
      </BLayout>
    </BHeightAdjuster>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onBeforeMount, DeepReadonly } from 'vue';
import { useRoute } from 'vue-router';
import { CallResult, Transcription as TTranscription } from '@/api/openapi';
import { CallResultApiService } from '@/api/user/resources/call_result';
import { FileStorageAccessApiService } from '@/api/user/resources/file_storage_access';
import CallResultComment from '@/components/organisms/user/transcription/CallResultComment.vue';
import SidePanel from '@/components/organisms/user/transcription/SidePanel.vue';
import TranscriptionHeader from '@/components/organisms/user/transcription/TranscriptionHeader.vue';
import TranscriptionMainContent from '@/components/organisms/user/transcription/TranscriptionMainContent.vue';
import { useAutoRetry } from '@/composable/use-auto-retry';
import { usePrevRoute } from '@/composable/user/prev-route';
import { useTranscription } from '@/composable/user/transcription/transcription';
import { useWait } from '@/composable/vue-wait';
import Transcription from './Transcription.vue';

const route = useRoute();
const callResultId = parseInt(route.params.callResultId as string);

const { goBack: backToLead, setPrevRoute, canGoBack } = usePrevRoute({
  fallbackRouteName: 'LeadList',
  fallbackQuery: () => ({ callTargetId: callResult.value.callTargetId }),
});
defineExpose({ setPrevRoute });
defineOptions({
  beforeRouteEnter(_to, from, next) {
    next((vm: InstanceType<typeof Transcription>) => {
      vm.setPrevRoute(from);
    });
  },
});

const { doActionWithWait, wait } = useWait();
const { transcription, transcriptionExists, isTranscriptionResultEmpty, transcriptionInProgress, rightAfterTranscription, fetchTranscription: fetchTranscriptionFromComposable } = useTranscription();

const transcriptionMainContent = ref<InstanceType<typeof TranscriptionMainContent>>();
const callResult = ref<CallResult>();
const callResultCommentLoading = ref<boolean>(false);
const summarizedResult = ref<string | undefined>();
const audioUrl = ref<string>();
const audioUnavailable = computed(() => {
  if (callResult.value == null) return false;
  return callResult.value.zoomPhoneRecording.expired
    || callResult.value.zoomPhoneRecording.processingStatus === 'failed'
    || callResult.value.zoomPhoneRecording.processingStatus === 'not_found';
});
const loading = computed(() => {
  return wait.is(['fetchCallResultWait']) || !!callResultCommentLoading.value;
});

const fetchCallResult = async () => {
  const api = new CallResultApiService();
  const isRetry = callResult.value != null;
  const waitKey = isRetry ? 'refetchCallResultWait' : 'fetchCallResultWait';
  await doActionWithWait(waitKey, async () => {
    const { data } = await api.getCallResult({
      request: { callResultId },
    });
    if (isRetry) {
      // NOTE: retryはここだけ上書きする（それ以外のデータを無事に保つため）
      callResult.value.zoomPhoneRecording = data.zoomPhoneRecording;
    } else {
      callResult.value = data;
    }
  });
};
const fetchTranscription = async () => {
  const zoomPhoneResultId = callResult.value?.zoomPhoneResultId;
  if (!zoomPhoneResultId) return;
  await fetchTranscriptionFromComposable(zoomPhoneResultId);
};
const createAudioUrl = async () => {
  if (callResult.value == null) return;
  if (audioUnavailable.value) return;
  if (callResult.value.zoomPhoneRecording.id == null) return;

  // データがまだない場合404をthrowするので止める
  try {
    await doActionWithWait('createAudioUrlWait', async () => {
      const apiService = new FileStorageAccessApiService();
      const { data } = await apiService.createZoomPhoneRecordingPresignedUrl({
        request: {
          createZoomPhoneRecordingPresignedUrlRequest: {
            zoomPhoneRecordingId: callResult.value.zoomPhoneRecording.id,
          },
        },
        errorHandlers: {
          404: () => {
            audioUrl.value = undefined;
          },
        },
      });
      audioUrl.value = data.url;
    });
  } catch (error) {
    if (error.response.status !== 404) {
      throw error;
    }
  }
};

const handleUpdateComment = async () => {
  await fetchCallResult();
};

const handleUpdateLoading = (loading: boolean) => {
  callResultCommentLoading.value = loading;
};

const handleUpdateSummarizedResult = (result: string | undefined) => {
  summarizedResult.value = result;
};

const playAudio = (time: number) => {
  transcriptionMainContent.value?.playAudio(time);
};

const checkCallResultAvailability = async (): Promise<boolean> => {
  await fetchCallResult();
  // NOTE: ZoomPhoneResultのデータがまだない場合があるのでその時はリトライ
  return callResult.value?.zoomPhoneRecording?.processingStatus !== 'processing' && callResult.value?.zoomPhoneRecording?.processingStatus !== 'not_started';
};
const { start: startCallResultRetry } = useAutoRetry(
  checkCallResultAvailability,
  {
    interval: 10000,
    maxRetries: 12, // 録音ダウンロードが遅れる場合がある（10秒 × 12回 = 約2分）
  },
);

const isAudioFetchFinished = (audioUrl: string | undefined) => audioUrl != null || audioUnavailable.value;
const checkAudioAvailability = async (): Promise<boolean> => {
  if (isAudioFetchFinished(audioUrl.value)) return true;

  await createAudioUrl();
  return isAudioFetchFinished(audioUrl.value);
};
const { start: startAudioRetry } = useAutoRetry(
  checkAudioAvailability,
  {
    interval: 10000,
    maxRetries: 12, // 録音ダウンロードが遅れる場合がある（10秒 × 12回 = 約2分）
  },
);

// Transcription取得の自動再取得
const isTranscriptionFetchFinished = (trs: DeepReadonly<TTranscription>) => trs?.id != null || trs?.processingStatus === 'failed';
const checkTranscriptionAvailability = async (): Promise<boolean> => {
  if (isTranscriptionFetchFinished(transcription.value)) return true;

  await fetchTranscription();
  return isTranscriptionFetchFinished(transcription.value);
};
const { start: startTranscriptionRetry } = useAutoRetry(
  checkTranscriptionAvailability,
  {
    interval: 15000,
    maxRetries: 20, // transcriptionは時間がかかる場合がある
  },
);

onBeforeMount(async () => {
  await startCallResultRetry();
  await startAudioRetry();
  await startTranscriptionRetry();
});
</script>

<style lang="scss" scoped>
.container {
  @include m-fixed-height(100vh);
  display: flex;
  position: relative;
  max-width: 100%;
  overflow: hidden;

  .adjuster {
    width: 100%;
    overflow: hidden;

    .header {
      @include m-fixed-height(50px);
      border-bottom: 1px solid $bdcolor-light;
      flex: 0 0 auto;
    }

    .body {
      overflow-y: hidden;
      flex: 1;

      .body-left {
        border-right: 1px solid $bdcolor-light;
      }

      .body-center {
        padding: $basespace-500;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .body-right {
        padding: $basespace-500;
        background-color: $bgcolor-base;
        border-left: 1px solid $bdcolor-light;
        overflow-y: auto;
      }
    }
  }
}
</style>
